import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type {
  FidsCoordinate,
  FidsTrackingRouteStatus,
} from 'contracts/FidsTrackingRoutes';
import type { RouteStep } from './RouteSteps';
import * as S from './styles';

interface ReviewStepProps {
  center: FidsCoordinate | null;
  warehouseCount: number;
  status: FidsTrackingRouteStatus;
  hasChanges: boolean;
  isBusy: boolean;
  onGoTo: (step: RouteStep) => void;
  onSave: () => void;
  onSaveAndActivate: () => void;
}

const plural = (count: number, singular: string, pluralForm: string) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

// Confere o que falta e ativa. O rascunho incompleto pode ser salvo para continuar depois.
export const ReviewStep: React.FC<ReviewStepProps> = ({
  center,
  warehouseCount,
  status,
  hasChanges,
  isBusy,
  onGoTo,
  onSave,
  onSaveAndActivate,
}) => {
  const items = [
    {
      step: 'center' as const,
      done: Boolean(center),
      text: center ? 'Centro marcado no mapa' : 'Falta marcar o centro no mapa',
    },
    {
      step: 'warehouses' as const,
      done: warehouseCount > 0,
      text:
        warehouseCount > 0
          ? `${plural(warehouseCount, 'armazém vinculado', 'armazéns vinculados')}`
          : 'Falta vincular pelo menos 1 armazém',
    },
  ];
  const isReady = items.every(({ done }) => done);

  return (
    <>
      <ul aria-label="O que o rastreio precisa" className="flex flex-col gap-2">
        {items.map(({ step, done, text }) => (
          <li
            key={step}
            className="flex items-center justify-between gap-2 text-sm"
          >
            <span
              className={`flex items-center gap-2 font-semibold ${done ? 'text-green-700' : 'text-amber-700'}`}
            >
              {done ? (
                <CheckCircle2 size={16} aria-hidden="true" />
              ) : (
                <AlertTriangle size={16} aria-hidden="true" />
              )}
              {text}
            </span>
            <button
              type="button"
              className="text-xs font-bold text-slate-600 underline"
              onClick={() => onGoTo(step)}
            >
              {done ? 'Alterar' : 'Fazer agora'}
            </button>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">
        Quando o caminhão entrar no anel de 10 km, o painel de chegadas confirma
        a chegada.
      </p>
      {status === 'active' ? (
        <>
          <p className="text-sm font-semibold text-green-700">
            Rastreio ativo: vale no painel para as viagens dos armazéns
            vinculados.
          </p>
          {hasChanges ? (
            <S.PrimaryButton
              type="button"
              disabled={isBusy || !isReady}
              onClick={onSave}
            >
              Salvar alterações
            </S.PrimaryButton>
          ) : null}
        </>
      ) : isReady ? (
        <S.PrimaryButton
          type="button"
          disabled={isBusy}
          onClick={onSaveAndActivate}
        >
          {hasChanges ? 'Salvar e ativar' : 'Ativar rastreio'}
        </S.PrimaryButton>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            Complete os itens acima para ativar. Você pode salvar o rascunho e
            continuar depois.
          </p>
          {hasChanges ? (
            <S.SecondaryButton type="button" disabled={isBusy} onClick={onSave}>
              Salvar rascunho
            </S.SecondaryButton>
          ) : null}
        </>
      )}
    </>
  );
};
